import { type CSSProperties, type Key, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { useResizeObserver } from "../../hooks/use-resize-observer";
import { clsx } from "../../utils/clsx";
import type { DiffItem } from "./diff";

interface TransitionGroupItemProps {
  className?: string;
  children?: ReactNode;
  duration?: number;
  itemKey: Key;
  stage: DiffItem["action"];
  onAnimationEnd: () => void;
  horizontalGap?: number;
  verticalGap?: number;
}

const ITEM_CLEANUP_INTERVAL = 100;

export const TransitionGroupItem = ({
  className,
  children,
  duration,
  itemKey,
  stage,
  onAnimationEnd,
  horizontalGap = 0,
  verticalGap = 0,
}: TransitionGroupItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);

  const [width, setWidth] = useState(-1);
  const [height, setHeight] = useState(-1);

  const onResize = useCallback(() => {
    if (!itemRef.current) {
      return;
    }

    const child = itemRef.current.children[0];
    if (!child) {
      return;
    }

    const bounds = child.getBoundingClientRect();

    setWidth(bounds.width + horizontalGap);
    setHeight(bounds.height + verticalGap);
  }, [horizontalGap, verticalGap]);
  useResizeObserver(itemRef.current, onResize);

  useEffect(() => {
    onResize();
  }, [onResize]);

  useEffect(() => {
    if (!itemRef.current) {
      return;
    }

    const div = itemRef.current;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      onAnimationEnd();
      return;
    }

    div.addEventListener("animationend", onAnimationEnd);

    return () => div.removeEventListener("animationend", onAnimationEnd);
  }, [onAnimationEnd]);

  useEffect(() => {
    if (!itemRef.current || stage === "keep") {
      return;
    }

    const div = itemRef.current;

    const interval = window.setInterval(() => {
      const animations = div.getAnimations();
      const allFinished = animations.every((animation) => animation.playState === "finished");

      if (allFinished && animations.length > 0) {
        onAnimationEnd();
      }
    }, ITEM_CLEANUP_INTERVAL);

    return () => clearInterval(interval);
  }, [onAnimationEnd, stage]);

  return (
    <div
      className={clsx(
        "data-enter:pointer-events-none data-enter:overflow-hidden data-leave:pointer-events-none data-leave:overflow-hidden",
        className,
      )}
      data-transition-item-key={itemKey}
      data-enter={stage === "add" ? true : undefined}
      data-leave={stage === "delete" ? true : undefined}
      style={
        {
          animationFillMode: "forwards",
          "--content-width": `${width}px`,
          "--content-height": `${height}px`,
          "--gap-x": `${horizontalGap}px`,
          "--gap-y": `${verticalGap}px`,
          paddingRight: `${horizontalGap}px`,
          paddingBottom: `${verticalGap}px`,
          animationDuration: duration ? `${duration}ms` : undefined,
        } as CSSProperties
      }
      ref={itemRef}
    >
      {children}
    </div>
  );
};
